/* Schema used in Liquid code for tracking click events on elements with data-content-id and data-content-type attributes
    {
      "type": "text",
      "id": "content_id",
      "label": "Analytics Campaign ID"
    },
    {
      "type": "select",
      "id": "content_type",
      "options": [
        {
          "value": "Hero Banner",
          "label": "Hero Banner"
        }
      ],
      "default": "Hero Banner",
      "label": "Analytics Content Type"
      },
      {
      "type": "header",
      "content": "Hero Banner Images and Link"
    }

    Attributes added to HTML links in the liquid template
 */
class DataTracker {
  constructor(gtagID = '') {
    this.gtagID = gtagID
    this.handleClick = this.handleClick.bind(this)
    this.attachedListeners = new Map()
    this._sectionData = []
    this.init()
    this.setupSectionDataWatcher()
  }

  init() {
    if (window.sectionData && Array.isArray(window.sectionData)) {
      this._sectionData = [...window.sectionData]
      this._sectionData.forEach((section) => this.attachListener(section))
    }
  }

  getSectionDataById(id) {
    return this._sectionData?.find((section) => section?.sectionId === id)
  }

  removeListener(sectionId) {
    const element = document.getElementById(sectionId)
    const handler = this.attachedListeners.get(sectionId)

    if (element && handler) {
      element.removeEventListener('click', handler)
      this.attachedListeners.delete(sectionId)
    }
  }

  replaceSectionDataById(id, data) {
    const index = this._sectionData?.findIndex((section) => section?.sectionId === id)
    if (index > -1) {
      // Clean up existing listener
      this.removeListener(id)

      // Replace the section data at the same index
      this._sectionData[index] = data

      // Attach new listener
      this.attachListener(data)
    }
  }

  setupSectionDataWatcher() {
    // Create a proxy for array methods
    const arrayHandler = {
      get: (target, property) => {
        // Handle array methods
        if (property === 'push') {
          return (...args) => {
            const result = Array.prototype.push.apply(target, args)
            // Attach listeners for newly added sections
            args.forEach((section) => this.attachListener(section))
            return result
          }
        }
        return target[property]
      },
      set: (target, property, value) => {
        target[property] = value
        // Only trigger change if it's not the length property
        if (property !== 'length') {
          // If setting an index, attach listener for that specific section
          if (!isNaN(property)) {
            this.attachListener(value)
          }
        }
        return true
      }
    }

    this._sectionData = new Proxy(this._sectionData, arrayHandler)

    // Watch window.sectionData
    Object.defineProperty(window, 'sectionData', {
      get: () => this._sectionData,
      set: (newValue) => {
        if (Array.isArray(newValue)) {
          // Clean up existing listeners
          this._sectionData.forEach((section) => {
            if (section.sectionId) {
              this.removeListener(section.sectionId)
            }
          })

          // Clear and repopulate the array
          this._sectionData.length = 0
          newValue.forEach((item) => {
            this._sectionData.push(item)
            this.attachListener(item)
          })
        }
        return true
      },
      configurable: true,
      enumerable: true
    })
  }

  attachListener(section) {
    if (!section || !section.sectionId || this.attachedListeners.has(section.sectionId)) {
      return
    }

    const element = document.getElementById(section.sectionId)
    if (element) {
      const boundHandler = (event) => this.handleClick(event, section)
      this.attachedListeners.set(section.sectionId, boundHandler)
      element.addEventListener('click', boundHandler)
    } else {
      console.warn(`Element with id ${section.sectionId} not found.`)
    }
  }

  handleClick(event, section) {
    if (section.eventTriggered) return
    if (section.customSelector) {
      const target = event.target.matches(section.customSelector)
        ? event.target
        : event.target.closest(section.customSelector)

      if (target) {
        this.pushDataLayer(section)
        section.eventTriggered = true
        return
      }
    }

    if (event.target.tagName === 'A' || event.target.closest('a')) {
      this.pushDataLayer(section)
      section.eventTriggered = true
    }
  }

  pushDataLayer(section) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'select_content',
      content_type: section.contentType,
      content_id: section.campaignId
    })
  }

  selectContentEvent(contentType, contentId) {
    if (this.gtagID && window.gtag) {
      gtag('event', 'select_content', {
        send_to: this.gtagID,
        content_type: contentType,
        content_id: contentId
      })
    } else {
      this.pushDataLayer({ contentType, campaignId: contentId })
    }
  }
}

export const dataTracker = new DataTracker()
